import { useQuery } from '@tanstack/react-query';

import { getTemporaryDocDetail } from '@entities/document/api/document';

/** 문서 상세 React Query 키 (액션 후 invalidate/refetch로 모달 갱신용) */
export const DOCUMENT_DETAIL_QUERY_KEY = 'documentDetail';

/**
 * useDocumentDetail 훅
 * - 문서 상세 정보를 React Query로 조회 (쿼리키: documentDetail)
 * - 결재/반려/코멘트 등 액션 후 invalidateQueries/refetchQueries 시 모달 내용이 즉시 갱신됨
 * - 로딩, 에러 상태 관리
 */
function useDocumentDetail(docIdx, isOpen) {
  const numericDocIdx = docIdx != null ? Number(docIdx) : null;
  const isValidIdx = numericDocIdx != null && !Number.isNaN(numericDocIdx) && numericDocIdx > 0;

  const query = useQuery({
    queryKey: [DOCUMENT_DETAIL_QUERY_KEY, numericDocIdx],
    queryFn: async function () {
      const response = await getTemporaryDocDetail(numericDocIdx);
      return response;
    },
    enabled: !!isOpen && !!isValidIdx,
    staleTime: 0,
  });

  const { data: response, isFetching, isError: queryIsError, error: queryError } = query;

  // 응답에서 docData / error 메시지 추출
  let docData = null;
  let error = null;

  if (response) {
    if (response.result_code === 0 && response.resultData) {
      docData = response.resultData;
    } else {
      error =
        response.error_msg ||
        response.resultData?.message ||
        `문서를 불러올 수 없습니다. (result_code: ${response.result_code})`;
    }
  }

  if (queryError) {
    const errorResponse = queryError.response?.data;
    error =
      errorResponse?.error_msg ||
      errorResponse?.message ||
      errorResponse?.resultData?.message ||
      (typeof errorResponse === 'string' ? errorResponse : null) ||
      '문서를 불러오는 중 오류가 발생했습니다.';
  }

  if (!isValidIdx && isOpen && docIdx != null) {
    error = '유효하지 않은 문서 인덱스입니다.';
  }

  return {
    isLoading: isFetching && !!isOpen,
    docData,
    error: error || null,
    refetch: query.refetch,
  };
}

export default useDocumentDetail;
